import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { DEFAULT_VISIT_SCHEDULE } from '../utils/defaultSchedule';

/**
 * Custom hook for managing user profile state and operations
 * Extracted from App.js to reduce component size
 * 
 * Manages: signature, MTF, rank, letterhead, mood, text size,
 * ROS/PE defaults, visit type, input mode, letter tone, personal info
 */
export const useProfile = (user, updateProfile) => {
  // Profile state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileSignature, setProfileSignature] = useState('');
  const [profileMTF, setProfileMTF] = useState('');
  const [profileRank, setProfileRank] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  
  // Letterhead fields (for exported documents)
  const [letterheadLine1, setLetterheadLine1] = useState('');
  const [letterheadLine2, setLetterheadLine2] = useState('');
  const [letterheadAddress, setLetterheadAddress] = useState('');
  
  // Default preferences
  const [defaultMood, setDefaultMood] = useState('');
  const [defaultTextSize, setDefaultTextSize] = useState(0);
  const [defaultIncludeROS, setDefaultIncludeROS] = useState(true);
  const [defaultIncludePE, setDefaultIncludePE] = useState(true);
  // Feb 26, 2026 — Profile-level medic-mode default. Starts encounters with
  // medic-mode pre-checked when true.
  const [defaultMedicMode, setDefaultMedicMode] = useState(false);
  const [defaultVisitType, setDefaultVisitType] = useState(() => {
    return localStorage.getItem('writeright_default_visit_type') || '';
  });
  const [defaultInputMode, setDefaultInputMode] = useState(() => {
    return localStorage.getItem('writeright_default_input_mode') || 'voice';
  });
  
  // Letter tone preference
  const [defaultLetterTone, setDefaultLetterTone] = useState('professional');
  
  // Visit type schedule (per day AM/PM)
  // Imported from shared util so the form-init reader and this Profile editor
  // see the SAME defaults — fixes "Thursday PM defaults to in-person on fresh
  // sessions" bug reported May 5, 2026.
  const DEFAULT_SCHEDULE = DEFAULT_VISIT_SCHEDULE;
  const [visitSchedule, setVisitSchedule] = useState(() => {
    try {
      const stored = localStorage.getItem('writeright_visit_schedule');
      return stored ? JSON.parse(stored) : DEFAULT_SCHEDULE;
    } catch { return DEFAULT_SCHEDULE; }
  });
  
  // Personal info for letters
  const [personalFirstName, setPersonalFirstName] = useState('');
  const [personalLastName, setPersonalLastName] = useState('');
  const [personalDOB, setPersonalDOB] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [personalOccupation, setPersonalOccupation] = useState('');

  // v6.8.44 — Saved Medical History / Injuries (patient mode).
  // Hydrated from user.saved_injuries and persisted on Save Profile.
  const [savedInjuries, setSavedInjuries] = useState([]);

  // Load profile from user data — returns loaded values for side-effect handling
  useEffect(() => {
    if (user) {
      setProfileSignature(user.signature_block || '');
      setProfileMTF(user.default_mtf || '');
      setProfileRank(user.default_rank || '');
      
      // Letterhead
      setLetterheadLine1(user.letterhead_line1 || '');
      setLetterheadLine2(user.letterhead_line2 || '');
      setLetterheadAddress(user.letterhead_address || '');
      
      setDefaultMood(user.default_mood || '');
      setDefaultTextSize(user.default_text_size || 0);

      // Feb 28, 2026 — hydrate visit_schedule from server (parity audit).
      // Previously visit_schedule was localStorage-only, so the AM/PM grid
      // reset to defaults on every new device login.
      if (user.visit_schedule && typeof user.visit_schedule === 'object'
          && Object.keys(user.visit_schedule).length > 0) {
        setVisitSchedule(user.visit_schedule);
      }
      
      // ROS/PE defaults
      const rosDefault = user.default_include_ros !== undefined ? user.default_include_ros : true;
      const peDefault = user.default_include_pe !== undefined ? user.default_include_pe : true;
      setDefaultIncludeROS(rosDefault);
      setDefaultIncludePE(peDefault);
      // Feb 26, 2026 — medic-mode default
      setDefaultMedicMode(!!user.default_medic_mode);
      
      // Letter tone
      if (user.default_letter_tone) {
        setDefaultLetterTone(user.default_letter_tone);
      }

      // Feb 28, 2026 — sync server-side default_auto_stream → localStorage.
      // Auto-stream toggle in Modals.jsx reads from localStorage; without
      // this sync, the toggle resets to "off" on every device/login because
      // localStorage is browser-local. Server value is the source of truth.
      if (user.default_auto_stream !== undefined) {
        try {
          localStorage.setItem(
            'writeright_auto_stream_default',
            user.default_auto_stream ? 'true' : 'false'
          );
          // Mirror to the legacy share-drafts flag so the team-share auto-
          // mirror picks up the same setting (existing convention).
          localStorage.setItem(
            'writeright_auto_share_drafts',
            user.default_auto_stream ? 'true' : 'false'
          );
        } catch { /* quota / private mode — ignore */ }
      }

      // Feb 28, 2026 — sync server-side use_genesis_dot_phrases → localStorage.
      // Same fix as auto-stream: the Modals toggle reads localStorage which
      // is browser-local, so without this the Dot-Phrase preference visually
      // resets to OFF on each new device/login even though the server value
      // is correct. Server is the source of truth.
      if (user.use_genesis_dot_phrases !== undefined) {
        try {
          localStorage.setItem(
            'writeright_use_genesis_dot_phrases',
            user.use_genesis_dot_phrases ? 'true' : 'false'
          );
        } catch { /* quota / private mode — ignore */ }
      }

      // Feb 28, 2026 — settings-panel parity audit. All four toggles below
      // were localStorage-only — same root cause as the auto-stream bug.
      // Now server→localStorage sync so they survive device changes.
      if (user.skip_sibling_cleanup !== undefined) {
        try { localStorage.setItem('writeright_skip_sibling_cleanup', user.skip_sibling_cleanup ? 'true' : 'false'); } catch { /* ignore */ }
      }
      if (user.high_contrast_mode !== undefined) {
        try {
          localStorage.setItem('writeright_high_contrast', user.high_contrast_mode ? 'true' : 'false');
          // Apply class immediately so the UI reflects the server value on
          // first paint without waiting for a manual toggle.
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('high-contrast', !!user.high_contrast_mode);
            document.body && document.body.classList.toggle('high-contrast', !!user.high_contrast_mode);
          }
        } catch { /* ignore */ }
      }
      if (user.pe_always_remarkable !== undefined) {
        try { localStorage.setItem('writeright_pe_always_remarkable', user.pe_always_remarkable ? 'true' : 'false'); } catch { /* ignore */ }
      }
      if (user.visit_schedule && typeof user.visit_schedule === 'object' && Object.keys(user.visit_schedule).length > 0) {
        try { localStorage.setItem('writeright_visit_schedule', JSON.stringify(user.visit_schedule)); } catch { /* ignore */ }
      }

      // Feb 28, 2026 — FULL SETTINGS PERSISTENCE AUDIT.
      // Hydrate every Profile/Settings preference from the server so
      // signing in on a new device restores the user's exact config.
      // Each block writes server → localStorage so the existing
      // localStorage-based readers (Modals.jsx toggles, FormatLockPill,
      // FloatingMicFAB, etc.) pick up the right value on first paint.
      // A `writeright:profile-synced` event is broadcast at the end so
      // components mounted before /auth/me lands re-read localStorage.
      try {
        if (user.minimalist_tiles !== undefined) {
          localStorage.setItem('writeright_minimalist_tiles', user.minimalist_tiles ? '1' : '0');
          if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-minimalist', user.minimalist_tiles ? '1' : '0');
          }
        }
        if (user.demo_mode !== undefined) {
          localStorage.setItem('writeright_demo_mode', user.demo_mode ? '1' : '0');
        }
        if (user.whitespace_disabled !== undefined) {
          localStorage.setItem('writeright_whitespace_disabled', user.whitespace_disabled ? 'true' : 'false');
        }
        if (user.skip_role_selector !== undefined) {
          localStorage.setItem('writeright_skip_role_selector', user.skip_role_selector ? 'true' : 'false');
        }
        if (user.clinical_tone) {
          localStorage.setItem('writeright_clinical_tone', user.clinical_tone);
        }
        if (user.pe_cv_language) {
          // Mar 3, 2026 — hydrate PE Unremarkable CV descriptor pref on login
          localStorage.setItem('writeright_pe_cv_language', user.pe_cv_language);
        }
        if (user.force_desktop_view !== undefined) {
          localStorage.setItem('writeright_desktop_view', user.force_desktop_view ? 'true' : 'false');
          // Apply DOM side effects immediately so the layout flips on
          // first paint without waiting for a manual toggle.
          if (typeof document !== 'undefined') {
            if (user.force_desktop_view) {
              document.body && document.body.classList.add('force-desktop-view');
            } else {
              document.body && document.body.classList.remove('force-desktop-view');
            }
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
              viewport.setAttribute('content', user.force_desktop_view
                ? 'width=1200, initial-scale=0.5, maximum-scale=2'
                : 'width=device-width, initial-scale=1');
            }
            try { window.dispatchEvent(new CustomEvent('viewmode-changed', { detail: !!user.force_desktop_view })); } catch { /* ignore */ }
          }
        }
        if (user.virtual_modality) {
          localStorage.setItem('writeright_virtual_modality', user.virtual_modality);
        }
        // Mar 1, 2026 — Hydrate last-used visit type + virtual modality so the
        // provider's bar reopens to the same encounter type they used most
        // recently. Provider's habitual workflow auto-restores across devices.
        if (user.last_visit_type) {
          localStorage.setItem('writeright_last_visit_type', user.last_visit_type);
        }
        if (user.last_virtual_modality) {
          localStorage.setItem('writeright_last_virtual_modality', user.last_virtual_modality);
        }
        if (user.recording_mode) {
          localStorage.setItem('writeright_recording_mode', user.recording_mode);
        }
        if (user.schedule_mode) {
          localStorage.setItem('writeright_schedule_mode', user.schedule_mode);
        }
        if (user.format_locks && typeof user.format_locks === 'object') {
          localStorage.setItem('writeright_format_locks', JSON.stringify(user.format_locks));
          // Mirror legacy A/P flag for backward compat with older readers.
          localStorage.setItem('writeright_format_lock_ap', user.format_locks.ap ? 'true' : 'false');
        }
        if (user.fab_mic_collapsed !== undefined) {
          localStorage.setItem('writeright_fab_mic_collapsed', user.fab_mic_collapsed ? '1' : '0');
        }
        if (user.floating_todo_open !== undefined) {
          localStorage.setItem('writeright_floating_todo_open', user.floating_todo_open ? '1' : '0');
        }
        if (user.floating_todo_position && typeof user.floating_todo_position === 'object') {
          try {
            localStorage.setItem('writeright_floating_todo_position', JSON.stringify(user.floating_todo_position));
          } catch (_) { /* ignore quota */ }
        }
        if (Array.isArray(user.header_button_order) && user.header_button_order.length) {
          localStorage.setItem('writeright_header_button_order', JSON.stringify(user.header_button_order));
        }
        if (user.active_team_id) {
          localStorage.setItem('writeright_active_team', user.active_team_id);
        }
        if (user.active_team_name) {
          localStorage.setItem('writeright_active_team_name', user.active_team_name);
        }
        if (user.team_member_name) {
          localStorage.setItem('writeright_team_member_name', user.team_member_name);
        }
      } catch { /* quota / private mode — ignore */ }

      // Feb 28, 2026 — hydrate ICD-10 chip-usage map from the server.
      // MERGE semantics: take max(local, server) per code so the existing
      // local taps (from offline / pre-sync sessions) are never lost when
      // we land on a fresh device. After merging, write back to BOTH
      // localStorage AND the server so all devices converge.
      if (user.icd10_usage && typeof user.icd10_usage === 'object') {
        try {
          const localKey = 'writeright_icd10_usage_v1';
          const local = JSON.parse(localStorage.getItem(localKey) || '{}');
          const merged = { ...local };
          Object.entries(user.icd10_usage).forEach(([code, count]) => {
            const n = Number(count) || 0;
            if (n <= 0) return;
            merged[code] = Math.max(Number(merged[code] || 0), n);
          });
          localStorage.setItem(localKey, JSON.stringify(merged));
          // Tell the ICD-10 chip-picker to refresh its in-memory ranking.
          window.dispatchEvent(new Event('writeright:icd10-usage-reset'));
          // If the merge produced bumps relative to the server, schedule
          // a flush so the server picks them up.
          const localBumped = Object.keys(merged).some(
            (c) => (Number(merged[c]) || 0) > (Number(user.icd10_usage[c]) || 0),
          );
          if (localBumped) {
            window.dispatchEvent(new Event('writeright:icd10-usage-flush'));
          }
        } catch { /* ignore */ }
      }

      // Feb 28, 2026 — broadcast a profile-synced event so any mounted
      // toggles (Settings modal, Team panel pills) re-read localStorage and
      // update their React state. Without this, components that initialize
      // useState from localStorage at mount-time stay stale until manually
      // re-toggled. Listened to by Modals.jsx and useAutosaveSession.
      try {
        window.dispatchEvent(new CustomEvent('writeright:profile-synced', {
          detail: {
            default_auto_stream: !!user.default_auto_stream,
            use_genesis_dot_phrases: !!user.use_genesis_dot_phrases,
            skip_sibling_cleanup: !!user.skip_sibling_cleanup,
            high_contrast_mode: !!user.high_contrast_mode,
            pe_always_remarkable: !!user.pe_always_remarkable,
            // Feb 28, 2026 — full audit additions:
            minimalist_tiles: !!user.minimalist_tiles,
            demo_mode: !!user.demo_mode,
            whitespace_disabled: !!user.whitespace_disabled,
            skip_role_selector: !!user.skip_role_selector,
            clinical_tone: user.clinical_tone || 'empathetic',
            force_desktop_view: !!user.force_desktop_view,
            virtual_modality: user.virtual_modality || 'video',
            recording_mode: user.recording_mode || 'solo',
            schedule_mode: user.schedule_mode || 'numbered',
            format_locks: user.format_locks || {},
            fab_mic_collapsed: !!user.fab_mic_collapsed,
            floating_todo_open: !!user.floating_todo_open,
            header_button_order: user.header_button_order || [],
            active_team_id: user.active_team_id || '',
            active_team_name: user.active_team_name || '',
            team_member_name: user.team_member_name || '',
          }
        }));
        // Fire format-lock-changed so the FormatLockPill re-reads its
        // localStorage state and updates the visible label.
        window.dispatchEvent(new CustomEvent('writeright:format-lock-changed', {
          detail: { locks: user.format_locks || {} },
        }));
      } catch { /* ignore */ }

      // Personal info
      if (user.first_name) setPersonalFirstName(user.first_name);
      if (user.last_name) setPersonalLastName(user.last_name);
      if (user.date_of_birth) setPersonalDOB(user.date_of_birth);
      if (user.phone) setPersonalPhone(user.phone);
      if (user.occupation) setPersonalOccupation(user.occupation);
      // v6.8.44 — Saved Medical History / Injuries
      if (Array.isArray(user.saved_injuries)) {
        setSavedInjuries(user.saved_injuries);
      }
    }
  }, [user]);

  // Save profile to server
  // onAfterSave callback allows App.js to apply side effects (sync to form state, etc.)
  const saveProfile = useCallback(async (onAfterSave) => {
    if (!user) return false;
    setIsSavingProfile(true);
    try {
      await updateProfile({ 
        signature_block: profileSignature, 
        default_mtf: profileMTF, 
        default_rank: profileRank,
        letterhead_line1: letterheadLine1,
        letterhead_line2: letterheadLine2,
        letterhead_address: letterheadAddress,
        default_mood: defaultMood,
        default_text_size: defaultTextSize,
        default_include_ros: defaultIncludeROS,
        default_include_pe: defaultIncludePE,
        default_medic_mode: defaultMedicMode,
        default_visit_type: defaultVisitType,
        default_input_mode: defaultInputMode,
        default_letter_tone: defaultLetterTone,
        first_name: personalFirstName,
        last_name: personalLastName,
        date_of_birth: personalDOB,
        phone: personalPhone,
        occupation: personalOccupation,
        // v6.8.44 — Saved Medical History / Injuries
        saved_injuries: savedInjuries,
        // Feb 28, 2026 — persist weekly visit-type schedule server-side so
        // it survives device changes. Previously this was localStorage-only
        // which forced re-entry of the whole AM/PM grid on every new login.
        visit_schedule: visitSchedule,
      });
      
      // Save to localStorage for immediate use
      localStorage.setItem('writeright_default_visit_type', defaultVisitType);
      localStorage.setItem('writeright_default_input_mode', defaultInputMode);
      localStorage.setItem('writeright_visit_schedule', JSON.stringify(visitSchedule));
      
      // Let App.js handle side effects (syncing to form state, etc.)
      if (onAfterSave) {
        onAfterSave({
          defaultIncludeROS,
          defaultIncludePE,
          defaultVisitType,
          defaultInputMode,
          personalFirstName,
          personalLastName,
          personalDOB,
          personalPhone,
          personalOccupation,
        });
      }
      
      toast.success('Profile saved!');
      // Feb 28, 2026 — also broadcast the breadcrumb "✓ Synced across devices"
      // badge so the header gives instant visual confirmation in addition to
      // the bottom toast. Same event listened to by ProfileSyncBadge.
      try {
        window.dispatchEvent(new CustomEvent('writeright:profile-saved', {
          detail: { success: true, label: 'Profile synced' },
        }));
      } catch { /* ignore */ }
      setShowProfileModal(false);
      return true;
    } catch (error) {
      toast.error('Failed to save profile');
      try {
        window.dispatchEvent(new CustomEvent('writeright:profile-saved', {
          detail: { success: false },
        }));
      } catch { /* ignore */ }
      return false;
    } finally {
      setIsSavingProfile(false);
    }
  }, [
    user, updateProfile, profileSignature, profileMTF, profileRank,
    letterheadLine1, letterheadLine2, letterheadAddress,
    defaultMood, defaultTextSize, defaultIncludeROS, defaultIncludePE,
    defaultVisitType, defaultInputMode, defaultLetterTone, visitSchedule,
    personalFirstName, personalLastName, personalDOB, personalPhone, personalOccupation
  ]);

  // Handle default visit type change (with toast + localStorage)
  const handleDefaultVisitTypeChange = useCallback((newType) => {
    setDefaultVisitType(newType);
    localStorage.setItem('writeright_default_visit_type', newType);
    toast.success(`Default visit type set to ${newType === 'virtual' ? 'Virtual' : newType === 'in_person_counseling' ? 'Counseling' : 'In-Person Exam'}`);
    return newType; // Return so caller can apply to current visitType
  }, []);

  // Handle default input mode change (with toast + localStorage)
  const handleDefaultInputModeChange = useCallback((newMode) => {
    setDefaultInputMode(newMode);
    localStorage.setItem('writeright_default_input_mode', newMode);
    toast.success(`Default input mode set to ${newMode === 'voice' ? 'Voice' : 'Type'}`);
    return newMode;
  }, []);

  return {
    // Modal
    showProfileModal,
    setShowProfileModal,
    
    // Profile state
    profileSignature, setProfileSignature,
    profileMTF, setProfileMTF,
    profileRank, setProfileRank,
    isSavingProfile,
    
    // Letterhead
    letterheadLine1, setLetterheadLine1,
    letterheadLine2, setLetterheadLine2,
    letterheadAddress, setLetterheadAddress,
    
    // Default preferences
    defaultMood, setDefaultMood,
    defaultTextSize, setDefaultTextSize,
    defaultIncludeROS, setDefaultIncludeROS,
    defaultIncludePE, setDefaultIncludePE,
    defaultMedicMode, setDefaultMedicMode,
    defaultVisitType, setDefaultVisitType,
    defaultInputMode, setDefaultInputMode,
    visitSchedule, setVisitSchedule,
    
    // Letter tone
    defaultLetterTone, setDefaultLetterTone,
    
    // Personal info
    personalFirstName, setPersonalFirstName,
    personalLastName, setPersonalLastName,
    personalDOB, setPersonalDOB,
    personalPhone, setPersonalPhone,
    personalOccupation, setPersonalOccupation,

    // v6.8.44 — Saved Medical History / Injuries
    savedInjuries, setSavedInjuries,

    // Functions
    saveProfile,
    handleDefaultVisitTypeChange,
    handleDefaultInputModeChange,
  };
};

export default useProfile;
